import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

/**
 * Interfaccia per i tipi di informazione contatto
 * Rappresenta i tipi disponibili (email, telefono, fax, PEC, ecc.)
 */
export interface ContactInformationType {
  Code?: number;
  Description?: string | null;
}

/**
 * Service per la gestione delle informazioni di contatto
 * 
 * Responsabilità:
 * - Recupera i tipi di informazione disponibili da file JSON statico
 * - Fornisce utility per lavorare con le informazioni di contatto
 * - Implementa caching per evitare letture multiple del file
 * 
 * Pattern utilizzato:
 * - Singleton service iniettato tramite DI
 * - Observable pattern per chiamate asincrone
 * - Caching in memoria per performance
 */
@Injectable()
export class ContactInformationService {

  /** Path del file JSON con i tipi di informazione */
  private dataUrl = `${window.location.origin}/assets/demo/contact-information-type.json`;
  
  /** Cache dei dati per evitare letture multiple del file */
  private cachedData: ContactInformationType[] | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Recupera la lista dei tipi di informazione contatto disponibili
   * (email, telefono, fax, PEC, ecc.)
   * 
   * Prima volta: Carica dal file JSON e salva in cache
   * Successive volte: Restituisce i dati dalla cache
   * 
   * @returns Observable<ContactInformationType[]> Array di tipi di informazione
   */
  getContactInformationTypes(): Observable<ContactInformationType[]> {

    // Se i dati sono già presenti nella cache, restituiscili direttamente
    if (this.cachedData) {
      return of(this.cachedData);
    }

    // Altrimenti, effettua la richiesta HTTP e memorizza i dati nella cache
    return this.http
      .get<ContactInformationType[]>(this.dataUrl)
      .pipe(
        map((data) => {
          this.cachedData = data;
          return data;
        }),
        catchError((error) => {
          console.error('Error loading contact information types from JSON:', error);
          return of([]); // Restituisci un array vuoto in caso di errore
        })
      );
  }

}
